import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../Contexts/AuthContext";
import {
  TbArrowsExchange,
  TbCircleMinus,
  TbCirclePlus,
  TbCoins,
  TbWallet,
} from "react-icons/tb";
import { getStablesApiErrorMessage, getStablesLimits } from "../Services/stablesApi";
import {
  getOperationLimitId,
  resolveLimitAvailableCents,
} from "../Pages/Private/Stables/StablesLimits/constants";

const LIMIT_ICONS = {
  "saque-onchain": TbCoins,
  swap: TbArrowsExchange,
  compra: TbCirclePlus,
  venda: TbCircleMinus,
  saque: TbWallet,
};

const attachIcons = (items = []) =>
  items.map((item) => ({
    ...item,
    icon: LIMIT_ICONS[item.id] ?? TbCoins,
  }));

const EMPTY_WITHDRAW = {
  label: "",
  usedCents: 0,
  totalCents: 0,
};

export const useStablesLimits = () => {
  const isAutenticated = useAuthStore((state) => state.isAutenticated);
  const token = useAuthStore((state) => state.token);
  const [items, setItems] = useState([]);
  const [withdrawOnchain, setWithdrawOnchain] = useState(EMPTY_WITHDRAW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getStablesLimits();
      if (!result.items?.length) throw new Error("Empty limits");
      setItems(attachIcons(result.items));
      setWithdrawOnchain(result.withdrawOnchain ?? EMPTY_WITHDRAW);
    } catch (err) {
      setItems([]);
      setWithdrawOnchain(EMPTY_WITHDRAW);
      setError(getStablesApiErrorMessage(err, "Não foi possível carregar os limites."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAutenticated || !token?.trim()) {
      setItems([]);
      setWithdrawOnchain(EMPTY_WITHDRAW);
      setError(null);
      setLoading(false);
      return;
    }
    load();
  }, [load, token, isAutenticated]);

  const summary = useMemo(() => {
    const totals = items.reduce(
      (acc, item) => ({
        usedCents: acc.usedCents + item.usedCents,
        totalCents: acc.totalCents + item.totalCents,
        availableCents: acc.availableCents + resolveLimitAvailableCents(item),
      }),
      { usedCents: 0, totalCents: 0, availableCents: 0 }
    );
    return totals;
  }, [items]);

  const getLimitForOperation = useCallback(
    (operation) => {
      if (operation === "withdraw") {
        if (withdrawOnchain?.label || withdrawOnchain?.totalCents) {
          return withdrawOnchain;
        }
        return items.find((item) => item.id === "saque-onchain") ?? EMPTY_WITHDRAW;
      }

      const limitId = getOperationLimitId(operation);
      if (!limitId) return EMPTY_WITHDRAW;

      return items.find((item) => item.id === limitId) ?? EMPTY_WITHDRAW;
    },
    [items, withdrawOnchain]
  );

  const getOperationLimitAvailableCents = useCallback(
    (operation) => resolveLimitAvailableCents(getLimitForOperation(operation)),
    [getLimitForOperation]
  );

  return {
    items,
    withdrawOnchain,
    summary,
    loading,
    error,
    reload: load,
    getLimitForOperation,
    getOperationLimitAvailableCents,
  };
};
